import { toNumber as toNullableNumber } from "./calc";

const fromDbProject = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    title: row.title ?? "",
    customer: row.customer ?? "",
    startAt: row.start_at ?? null,
    disconnectLocation: row.disconnect_location ?? "",
    connectLocation: row.connect_location ?? "",
    rig: row.rig ?? "",
    vessel: row.vessel ?? "",
    logoUrl: row.logo_url ?? "",
  };
};

const truncate = (str, max) => (str && str.length > max ? str.slice(0, max) : str);
const emptyToNull = (str) => (str === "" || str == null ? null : str);

const toDbProjectUpdate = (project) => {
  if (!project.title?.trim()) throw new Error("Project title is required");
  return {
    title: truncate(project.title.trim(), 200),
    customer: emptyToNull(truncate(project.customer, 200)),
    start_at: project.startAt || null,
    disconnect_location: emptyToNull(truncate(project.disconnectLocation, 200)),
    connect_location: emptyToNull(truncate(project.connectLocation, 200)),
    rig: emptyToNull(truncate(project.rig, 200)),
    vessel: emptyToNull(truncate(project.vessel, 200)),
    logo_url: project.logoUrl || null,
  };
};

const fromDbEvent = (row) => ({
  id: row.id,
  projectId: row.project_id,
  line: row.line ?? "",
  operation: row.operation ?? "",
  estimatedHours:
    row.estimated_hours === null || row.estimated_hours === undefined
      ? ""
      : String(row.estimated_hours),
  actualHours:
    row.actual_hours === null || row.actual_hours === undefined
      ? ""
      : String(row.actual_hours),
  startAt: row.start_at ?? null,
  endAt: row.end_at ?? null,
  remarks: row.remarks ?? "",
  sortIndex: row.sort_index ?? 0,
});

const toDbEventUpdate = (event) => ({
  line: event.line || null,
  operation: event.operation || null,
  estimated_hours: toNullableNumber(event.estimatedHours),
  actual_hours: toNullableNumber(event.actualHours),
  start_at: event.startAt || null,
  end_at: event.endAt || null,
  remarks: event.remarks || null,
  sort_index: event.sortIndex ?? 0,
});

// --- DPR Export ---

const fromDbDpr = (row) => ({
  id: row.id,
  projectId: row.project_id,
  userId: row.user_id,
  dprNumber: row.dpr_number,
  reportDate: row.report_date,
  summaryLast24h: row.summary_last_24h ?? "",
  summaryNext24h: row.summary_next_24h ?? "",
  pdfUrl: row.pdf_url ?? null,
  createdAt: row.created_at,
});

const toDbDprInsert = (dpr) => ({
  project_id: dpr.projectId,
  user_id: dpr.userId,
  dpr_number: dpr.dprNumber,
  report_date: dpr.reportDate,
  summary_last_24h: dpr.summaryLast24h || "",
  summary_next_24h: dpr.summaryNext24h || "",
});

const toDbDprUpdate = (dpr) => ({
  summary_last_24h: dpr.summaryLast24h || "",
  summary_next_24h: dpr.summaryNext24h || "",
});

// --- Weather ---

const fromDbWeather = (row) => ({
  id: row.id,
  dprId: row.dpr_id,
  timeSlot: row.time_slot,
  windSpeed: row.wind_speed,
  windDir: row.wind_dir ?? "",
  windHeight: row.wind_height,
  seaPeriod: row.sea_period,
  seaHeight: row.sea_height,
  swellDir: row.swell_dir ?? "",
  swellPeriod: row.swell_period,
  pitch: row.pitch,
  roll: row.roll,
});

const toDbWeatherUpsert = (entry) => {
  const row = {
    dpr_id: entry.dprId,
    time_slot: entry.timeSlot,
    wind_speed: toNullableNumber(entry.windSpeed),
    wind_dir: entry.windDir || null,
    wind_height: toNullableNumber(entry.windHeight),
    sea_period: toNullableNumber(entry.seaPeriod),
    sea_height: toNullableNumber(entry.seaHeight),
    swell_dir: entry.swellDir || null,
    swell_period: toNullableNumber(entry.swellPeriod),
    pitch: toNullableNumber(entry.pitch),
    roll: toNullableNumber(entry.roll),
  };
  if (entry.id) row.id = entry.id;
  return row;
};

// --- HSSEQ ---

const fromDbHsseq = (row) => ({
  id: row.id,
  dprId: row.dpr_id,
  category: row.category,
  details: row.details ?? "N/A",
});

const toDbHsseqUpsert = (entry) => {
  const row = {
    dpr_id: entry.dprId,
    category: entry.category,
    details: entry.details || "N/A",
  };
  if (entry.id) row.id = entry.id;
  return row;
};

// --- Detailed Log ---

const fromDbLogEntry = (row) => ({
  id: row.id,
  dprId: row.dpr_id,
  timestamp: row.timestamp,
  description: row.description ?? "",
});

const toDbLogEntryInsert = (entry) => ({
  dpr_id: entry.dprId,
  timestamp: entry.timestamp,
  description: entry.description,
});

// --- Personnel ---

const fromDbPersonnel = (row) => ({
  id: row.id,
  dprId: row.dpr_id,
  name: row.name ?? "",
  position: row.position ?? "",
  locationToday: row.location_today ?? "",
  locationTomorrow: row.location_tomorrow ?? "",
  sortOrder: row.sort_order ?? 0,
});

const toDbPersonnelInsert = (entry) => ({
  dpr_id: entry.dprId,
  name: entry.name,
  position: entry.position,
  location_today: entry.locationToday || "",
  location_tomorrow: entry.locationTomorrow || "",
  sort_order: entry.sortOrder ?? 0,
});

// --- Tool/Equipment ---

const fromDbTool = (row) => ({
  id: row.id,
  dprId: row.dpr_id,
  itemId: row.item_id ?? "",
  description: row.description ?? "",
  vessel: row.vessel ?? "",
  sortOrder: row.sort_order ?? 0,
});

const toDbToolInsert = (entry) => ({
  dpr_id: entry.dprId,
  item_id: entry.itemId,
  description: entry.description,
  vessel: entry.vessel || "",
  sort_order: entry.sortOrder ?? 0,
});

export {
  fromDbProject,
  toDbProjectUpdate,
  fromDbEvent,
  toDbEventUpdate,
  fromDbDpr,
  toDbDprInsert,
  toDbDprUpdate,
  fromDbWeather,
  toDbWeatherUpsert,
  fromDbHsseq,
  toDbHsseqUpsert,
  fromDbLogEntry,
  toDbLogEntryInsert,
  fromDbPersonnel,
  toDbPersonnelInsert,
  fromDbTool,
  toDbToolInsert,
};
